import React, { useContext, useEffect, useState } from "react"
import { Button, Form, Modal } from "react-bootstrap"
import { useDispatch, useSelector } from "react-redux"
import { Formik } from "formik"

import ErrorBoundary from "@/components/ErrorBoundary"
import { SubmittingContext, useProductSubmitting } from "@/onboarding/features/product/helpers"
import SaveButton from "@/components/buttons/SaveButton"
import { getForm, getProductId, isProductReady } from "@/onboarding/features/product/selectors"
import Api from "@/api"
import AsyncSearch, { DataStates } from "@/helpers/AsyncSearch"
import { setApplicableProducts } from "@/onboarding/features/product/Slice"

import SubmittingSpinner from "./SubmittingSpinner"

const Content = ({ onSubmit, onHide }) => {
  const dispatch = useDispatch()
  const id = useSelector(getProductId)
  const { applicableProducts } = useSelector(getForm)
  const [submitting, setSubmitting] = useContext(SubmittingContext)
  // AsyncSearch leaves data-state tracking to the caller: without these props
  // its placeholder reads "Loading..." forever, even after options arrive.
  const [dataState, setDataState] = useState(DataStates.LOADING)

  return (
    <Formik
      initialValues={{ applicableProducts }}
      onSubmit={(values) => {
        setSubmitting(true)
        dispatch(setApplicableProducts({ applicableProducts: values.applicableProducts }))
        onSubmit()
      }}
    >
      {({ values, setValues, handleSubmit }) => (
        <Form className="d-flex flex-column gap-3" onSubmit={handleSubmit}>
          <div>
            <p className="mb-0">Save and also copy the changes to the tours selected below.</p>
          </div>
          <Form.Group>
            <Form.Label>Tours</Form.Label>
            <AsyncSearch
              selectedValues={values.applicableProducts}
              setSelectedValues={(applicableProducts) => setValues({ applicableProducts })}
              getOptions={(query) => Api.onboardingProduct.searchApplicableProducts(id, query)}
              onOptionsLoaded={setDataState}
              dataState={dataState}
              absentDataMessage="No other tours share this tour code."
            />
          </Form.Group>
          <div className="d-flex gap-2">
            <SaveButton
              type="submit"
              disabled={values.applicableProducts.length < 1 || submitting}
            />
            <Button size="sm" variant="outline-danger" onClick={onHide} disabled={submitting}>
              <i className="fas fa-times me-2" />
              Cancel
            </Button>
            <SubmittingSpinner />
          </div>
        </Form>
      )}
    </Formik>
  )
}

const ApplyChangesModal = ({
  show,
  onHide,
  onSubmit,
}: {
  show: boolean
  onHide: () => void
  onSubmit: any
}): JSX.Element => {
  const productReady = useSelector(isProductReady)
  const [submitting, setSubmitting] = useProductSubmitting()
  const [submitted, setSubmitted] = useState(false)

  useEffect(() => {
    if (submitting && productReady) {
      setSubmitted(true)
    }

    if (submitted && productReady) {
      onHide()
    }
  }, [submitting, productReady])

  return (
    <SubmittingContext.Provider value={[submitted, setSubmitting]}>
      <Modal
        show={show}
        onHide={onHide}
        size="lg"
        aria-labelledby="apply-changes-modal-title"
        centered
      >
        <Modal.Header>
          <Modal.Title className="w-100" id="edit-modal-title">
            <div className="d-flex justify-content-between">
              <span>Apply Changes</span>
              <span className="cursor-pointer" onClick={onHide}>
                <i className="fas fa-times" />
              </span>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ErrorBoundary>
            <Content onSubmit={onSubmit} onHide={onHide} />
          </ErrorBoundary>
        </Modal.Body>
      </Modal>
    </SubmittingContext.Provider>
  )
}

export default ApplyChangesModal
